import { FetchFn, loadSvg } from "../loader";
import { BarOptions, BarToFontSizeMap, ZeusBarFontFamilyMap, ZeusBarTextColorMap } from "./consts";

export async function renderCollarText(
  options: BarOptions,
  angle: number,
  doc: Document,
  fetch: FetchFn,
  sku: string,
  svg?: HTMLElement
) {
  if (!svg) {
    svg = (await loadSvg("/svc_spa/zeus_assets/bar/collar.svg", doc, fetch)).cloneNode(true) as HTMLElement;
  }

  if (!svg) {
    throw new Error("Unable to create collar SVG");
  }

  const color = ZeusBarTextColorMap[options.collarColor?.val];

  // Set the color
  const textEls = svg.querySelectorAll("text");
  textEls.forEach((el) => el.setAttribute("style", `color: ${color}`));

  if (angle === 1) {
    // Closeup Angle
    svg.querySelector("#bottom-text")?.setAttribute("mask", "url(#custom-collar-text-mask-1)");
    if (sku === "CUSTOMOHIOPOWER_NEW") {
      svg.setAttribute(
        "style",
        "transform: scale(0.78) translateX(-0.5%) translateY(6%) rotateX(-34deg) rotateY(42deg) skew(0deg, -3deg)"
      );
    }
    if (sku === "CUSTOMBELLA_NEW") {
      svg.setAttribute(
        "style",
        "transform: scale(0.81) translateX(-5%) translateY(-2%) rotateX(-34deg) rotateY(40deg) skew(0deg, -3deg)"
      );
    }
    if (sku === "CUSTOMOHIO_NEW") {
      svg.setAttribute(
        "style",
        "transform: scale(0.81) translateX(-5%) translateY(-2%) rotateX(-34deg) rotateY(40deg) skew(0deg, -3deg)"
      );
    }
  } else if (angle === 2) {
    // Far Away
    svg.querySelector("#bottom-text")?.setAttribute("mask", "url(#custom-collar-text-mask-2)");
    if (sku === "CUSTOMOHIOPOWER_NEW") {
      svg.setAttribute("style", "transform: translateX(39.1%) translateY(-41%) scale(0.120)");
    }
    if (sku === "CUSTOMBELLA_NEW") {
      svg.setAttribute("style", "transform: translateX(36.60%) translateY(-37.75%) scale(0.123)");
    }
    if (sku === "CUSTOMOHIO_NEW") {
      svg.setAttribute("style", "transform: translateX(39.25%) translateY(-41.25%) scale(0.105)");
    }
  }

  const topEl = svg.querySelector("#top-path");
  const bottomEl = svg.querySelector("#bottom-path");

  const style = `font-size: 90px; fill: ${color}; font-weight: bold; font-family: ${options.collarFont?.val}; letter-spacing: 5px;`;

  if (!topEl || !bottomEl) {
    throw new Error("Unable to find top / bottom text paths");
  }

  topEl.innerHTML = options.collarTextTop?.val || "";
  topEl.setAttribute("style", style);
  bottomEl.innerHTML = options.collarTextBottom?.val || "";
  bottomEl.setAttribute("style", style);

  return { svg };
}

export async function renderShaftText(options: BarOptions, doc: Document, fetch: FetchFn, svg?: HTMLElement) {
  if (!svg) {
    svg = (await loadSvg("/svc_spa/zeus_assets/bar/shaft.svg", doc, fetch)).cloneNode(true) as HTMLElement;
  }

  const textEl = svg.querySelector("#shaft-text") as SVGTextPathElement;

  if (!textEl) {
    throw new Error("Invalid text path");
  }

  const color = ZeusBarTextColorMap[options.shaftColor?.val];
  let fontsize = 65;
  const style = (fontSize: number) =>
    `font-size: ${fontSize}px; fill: ${color}; font-weight: bold; font-family: ${options.shaftFont.val}`;

  textEl.setAttribute("style", style(fontsize));
  textEl.setAttribute("font-size", `${fontsize}px`);
  while (textEl.getComputedTextLength() >= 500 && fontsize > 50) {
    fontsize -= 1;
    textEl.setAttribute("style", style(fontsize));
    textEl.setAttribute("font-size", `${fontsize}px`);
  }
  textEl.innerHTML = options.shaftText.val || "";

  return { svg };
}

export async function renderShaftMultiText(options: BarOptions, doc: Document, fetch: FetchFn, svg?: HTMLElement) {
  if (!svg) {
    svg = (await loadSvg("/svc_spa/zeus_assets/bar/shaft.svg", doc, fetch)).cloneNode(true) as HTMLElement;
  }

  const leftTextEl = svg.querySelector("#shaft-text-left") as SVGTextPathElement;
  const rightTextEl = svg.querySelector("#shaft-text-right") as SVGTextPathElement;

  if (!leftTextEl || !rightTextEl) {
    throw new Error("Invalid paths on shaft");
  }

  const color = ZeusBarTextColorMap[options.shaftColor?.val];
  let leftFontsize = 65;
  let rightFontsize = 65;
  const style = (fontsize: number) =>
    `font-size: ${fontsize}px; fill: ${color}; font-weight: bold; font-family: ${options.shaftFont.val}`;

  leftTextEl.setAttribute("style", style(leftFontsize));
  leftTextEl.setAttribute("font-size", `${leftFontsize}px`);
  leftTextEl.innerHTML = options.shaftTextLeft.val || "";

  rightTextEl.setAttribute("style", style(rightFontsize));
  rightTextEl.setAttribute("font-size", `${rightFontsize}px`);
  rightTextEl.innerHTML = options.shaftTextRight.val || "";
  while (leftTextEl.getComputedTextLength() >= 500 && leftFontsize > 30) {
    leftFontsize -= 3;
    leftTextEl.setAttribute("style", style(leftFontsize));
    leftTextEl.setAttribute("font-size", `${leftFontsize}px`);
  }
  while (rightTextEl.getComputedTextLength() >= 500 && rightFontsize > 30) {
    rightFontsize -= 3;
    rightTextEl.setAttribute("style", style(rightFontsize));
    rightTextEl.setAttribute("font-size", `${rightFontsize}px`);
  }

  return { svg };
}

export async function renderShaftMultiImage(options: BarOptions, doc: Document, fetch: FetchFn, svg?: HTMLElement) {
  if (!svg) {
    svg = (await loadSvg("/svc_spa/zeus_assets/bar/shaft.svg", doc, fetch)).cloneNode(true) as HTMLElement;
  }

  const leftImageEl = svg.querySelector("#custom-shaft-image-left");
  const rightImageEl = svg.querySelector("#custom-shaft-image-right");

  if (!leftImageEl || !rightImageEl) {
    throw new Error("Invalid SVG for shaft multi image");
  }

  const color = ZeusBarTextColorMap[options.shaftColor?.val];
  const style = `fill: ${color};`;
  if (options.shaftImageLeft.val) {
    leftImageEl.innerHTML = options.shaftImageLeft.val || "";

    const leftSvgEl = leftImageEl.firstElementChild;
    if (leftSvgEl) {
      const svgWidthMatch = options.shaftImageLeft.val.match(/width="(.*?)"/);
      if (svgWidthMatch) {
        const svgWidth = parseInt(svgWidthMatch[1], 10);
        const x = ((1600 * 0.18 - svgWidth / 2) / 1600) * 100;
        leftSvgEl.setAttribute("x", `${x}%`);
      } else {
        leftSvgEl.setAttribute("x", "18%");
      }
      leftSvgEl.setAttribute("style", style);
      leftSvgEl.setAttribute("y", "42.5%");
    }
  }

  if (options.shaftImageRight.val) {
    rightImageEl.innerHTML = options.shaftImageRight.val || "";

    const rightSvgEl = rightImageEl.firstElementChild;
    if (rightSvgEl) {
      const svgWidthMatch = options.shaftImageRight.val.match(/width="(.*?)"/);
      if (svgWidthMatch) {
        const svgWidth = parseInt(svgWidthMatch[1], 10);
        const x = ((1600 * 0.8 - svgWidth / 2) / 1600) * 100;
        rightSvgEl.setAttribute("x", `${x}%`);
      } else {
        rightSvgEl.setAttribute("x", "78%");
      }
      rightSvgEl.setAttribute("style", style);
      rightSvgEl.setAttribute("y", "42%");
    }
  }

  return { svg };
}

export async function renderShaftImage(options: BarOptions, doc: Document, fetch: FetchFn, svg?: HTMLElement) {
  if (!svg) {
    svg = (await loadSvg("/svc_spa/zeus_assets/bar/shaft.svg", doc, fetch)).cloneNode(true) as HTMLElement;
  }

  const imageEl = svg.querySelector("#custom-shaft-image");
  if (!imageEl) {
    throw new Error("Unable to find shaft image el");
  }

  const color = ZeusBarTextColorMap[options.shaftColor?.val];
  const style = `fill: ${color};`;
  imageEl.innerHTML = options.shaftImage.val || "";
  const svgEl = imageEl?.firstElementChild;

  if (!svgEl) {
    throw new Error("Unable to find shaft image el");
  }

  svgEl.setAttribute("style", style);

  const svgWidthMatch = options.shaftImage.val.match(/width="(.*?)"/);

  if (!svgWidthMatch) {
    svgEl.setAttribute("x", "50%");
    svgEl.setAttribute("y", "46%");

    return { svg };
  }
  const svgWidth = parseInt(svgWidthMatch[1], 10);
  const x = ((800 - svgWidth / 2) / 1600) * 100;
  svgEl.setAttribute("x", `${x}%`);
  svgEl.setAttribute("y", "46%");

  return { svg };
}

function centerImageInGElement(el: SVGGElement, yTransform = 0) {
  const width = el.firstElementChild?.getAttribute("width") || el.getBoundingClientRect().width;

  if (!width) {
    return;
  }

  const leftTransform = -1 * parseInt(`${width}`, 10) *  0.5;
  el.setAttribute("transform", `translate(${leftTransform},${yTransform})`);
}

export async function getStencil(sku: string, options: BarOptions, fetch: FetchFn, doc: Document) {
  if (sku === "CUSTOMOHIO_NEW") {
    if (options.shaftText.val) {
      let stencilUrl = "/svc_spa/zeus_assets/bar/ohio_bar_stencil.svg";
      if (options.shaftText.val.length > 19) {
        stencilUrl = "/svc_spa/zeus_assets/bar/ohio_bar_max_width_stencil.svg";
      }
      const svg = (await loadSvg(stencilUrl, doc, fetch)).cloneNode(true) as HTMLElement;
      const textEl = svg.querySelector("#text");
      if (!textEl) {
        throw new Error("Unable to find shaft text el");
      }
      const style = `font-family: ${ZeusBarFontFamilyMap[options.shaftFont.val]}; font-size: ${BarToFontSizeMap[sku]};`;
      textEl.innerHTML = options.shaftText.val;
      textEl.setAttribute("style", style);

      return svg;
    }

    if (options.shaftImage.val) {
      let stencilUrl = "/svc_spa/zeus_assets/bar/ohio_bar_stencil.svg";
      const svgWidthMatch = options.shaftImage.val.match(/width="(.*?)"/)
      if(svgWidthMatch && svgWidthMatch[1]){
        const svgWidth = parseInt(svgWidthMatch[1],10)
        if(svgWidth > 1000){
          stencilUrl = "/svc_spa/zeus_assets/bar/ohio_bar_max_width_stencil.svg";
        }
      }
      const svg = (await loadSvg(stencilUrl, doc, fetch)).cloneNode(
        true
      ) as HTMLElement;
      const imageEl = svg.querySelector("#image") as SVGGElement;
      if (!imageEl) {
        throw new Error("Unable to find shaft image el");
      }
      imageEl.innerHTML = options.shaftImage.val;
      // x="449.96" y="449.96" width="108" height="108"
      imageEl.setAttribute("fill", "white");
      imageEl.firstElementChild?.setAttribute("x", "50%");
      imageEl.firstElementChild?.setAttribute("y", "51.73");

      centerImageInGElement(imageEl);

      return svg;
    }
  }
  if (sku === "CUSTOMBELLA_NEW") {
    if (options.shaftText.val) {
      let stencilUrl = "/svc_spa/zeus_assets/bar/bella_bar_stencil.svg";
      if (options.shaftText.val.length > 19) {
        stencilUrl = "/svc_spa/zeus_assets/bar/bella_bar_max_width_stencil.svg";
      }
      const svg = (await loadSvg(stencilUrl, doc, fetch)).cloneNode(true) as HTMLElement;
      const textEl = svg.querySelector("#text");
      if (!textEl) {
        throw new Error("Unable to find shaft text el");
      }
      const style = `font-family: ${ZeusBarFontFamilyMap[options.shaftFont.val]}; font-size: ${BarToFontSizeMap[sku]};`;
      textEl.innerHTML = options.shaftText.val;
      textEl.setAttribute("style", style);

      return svg;
    }
    if (options.shaftImage.val) {
      let stencilUrl = "/svc_spa/zeus_assets/bar/bella_bar_stencil.svg";
      const svgWidthMatch = options.shaftImage.val.match(/width="(.*?)"/)
      if(svgWidthMatch && svgWidthMatch[1]){
        const svgWidth = parseInt(svgWidthMatch[1],10)
        if(svgWidth > 1000){
          stencilUrl = "/svc_spa/zeus_assets/bar/bella_bar_max_width_stencil.svg";
        }
      }
      const svg = (await loadSvg(stencilUrl, doc, fetch)).cloneNode(
        true
      ) as HTMLElement;
      const imageEl = svg.querySelector("#image") as SVGGElement;
      if (!imageEl) {
        throw new Error("Unable to find shaft image el");
      }
      imageEl.innerHTML = options.shaftImage.val;
      imageEl.setAttribute("fill", "white");
      imageEl.firstElementChild?.setAttribute("x", "50%");
      imageEl.firstElementChild?.setAttribute("y", "23.12");

      centerImageInGElement(imageEl, 13);
      return svg;
    }
  }
  if (sku === "CUSTOMOHIOPOWER_NEW") {
    if (options.shaftTextRight.val || options.shaftTextLeft.val) {
      const svg = (await loadSvg("/svc_spa/zeus_assets/bar/power_bar_stencil.svg", doc, fetch)).cloneNode(
        true
      ) as HTMLElement;
      const leftTextEl = svg.querySelector("#left-text");
      const rightTextEl = svg.querySelector("#right-text");
      if (!leftTextEl || !rightTextEl) {
        throw new Error("Unable to find shaft text el");
      }
      const style = `font-family: ${ZeusBarFontFamilyMap[options.shaftFont.val]}; font-size: ${BarToFontSizeMap[sku]};`;
      leftTextEl.innerHTML = options.shaftTextLeft.val;
      leftTextEl.setAttribute("style", style);

      rightTextEl.innerHTML = options.shaftTextRight.val;
      rightTextEl.setAttribute("style", style);

      return svg;
    }
    if (options.shaftImageRight.val || options.shaftImageLeft.val) {
      const svg = (await loadSvg("/svc_spa/zeus_assets/bar/power_bar_stencil.svg", doc, fetch)).cloneNode(
        true
      ) as HTMLElement;
      const leftImgEl = svg.querySelector("#left-image") as SVGGElement;
      const rightImgEl = svg.querySelector("#right-image") as SVGGElement;
      if (!leftImgEl || !rightImgEl) {
        throw new Error("Unable to find shaft text el");
      }
      leftImgEl.innerHTML = options.shaftImageLeft.val;
      rightImgEl.innerHTML = options.shaftImageRight.val;
      leftImgEl.setAttribute("fill", "white");
      rightImgEl.setAttribute("fill", "white");

      leftImgEl.firstElementChild?.setAttribute("x", "220");
      leftImgEl.firstElementChild?.setAttribute("y", "50");

      rightImgEl.firstElementChild?.setAttribute("x", "410");
      rightImgEl.firstElementChild?.setAttribute("y", "355");

      centerImageInGElement(rightImgEl);
      centerImageInGElement(leftImgEl);
      return svg;
    }
  }
  return null;
}
